import { computed, DestroyRef, effect, inject, Injectable, Injector, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractMasterViewDashboardService, FeatureMasterViewEvent } from '@iot-platform/feature/master-view';
import { IotToolbarDispatchActionType } from '@iot-platform/iot-platform-ui';
import { IotToolbarEvent } from '@iot-platform/models/common';
import { Dashboard } from '@iot-platform/models/dashboards';
import { catchError, finalize, of, tap } from 'rxjs';
import { AbstractI4bDashboardsService } from './abstract-i4b-dashboards.service';

@Injectable({
  providedIn: 'root'
})
export class I4bMasterViewDashboardService extends AbstractMasterViewDashboardService {
  allDashboards: Signal<Dashboard[]>;
  dashboardsLoading: WritableSignal<boolean> = signal(false);
  currentDashboard: WritableSignal<Dashboard | undefined> = signal(undefined);
  dashboardsConfiguration: Signal<{
    sortedDashboards: Dashboard[];
    currentDashboard: Dashboard | undefined;
    isDashboardsLoading: boolean;
  }> = computed(() => ({
    sortedDashboards: this.allDashboards(),
    currentDashboard: this.currentDashboard(),
    isDashboardsLoading: this.dashboardsLoading()
  }));
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly injector: Injector = inject(Injector);

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(protected readonly i4BDashboardService: AbstractI4bDashboardsService) {
    super();

    this.allDashboards = toSignal(
      this.i4BDashboardService.getDashboards().pipe(
        tap(() => this.dashboardsLoading.set(true)),
        finalize(() => this.dashboardsLoading.set(false)),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef)
      )
    ) as Signal<Dashboard[]>;

    effect(() => {
      const dashboards = this.allDashboards();
      const currentDashboard = this.currentDashboard();
      if (dashboards?.length > 0 && !currentDashboard) {
        this.currentDashboard.set(dashboards[0]);
      }
    });
  }

  getDashboardConfiguration(): Signal<{
    sortedDashboards: Dashboard[];
    currentDashboard: Dashboard | undefined;
    isDashboardsLoading: boolean;
  }> {
    return this.dashboardsConfiguration;
  }

  onToolbarEvent(_event: FeatureMasterViewEvent) {
    const event: IotToolbarEvent = _event.event;
    switch (event.type) {
      case IotToolbarDispatchActionType.APPLY_DASHBOARD as string:
        this.onApplyDashboard(event.options.dashboard);
        break;
    }
  }

  onApplyDashboard(dashboard: Dashboard) {
    this.currentDashboard.set(dashboard);
  }
}
